import { type JSX, useCallback, useMemo } from "react";
import styled from "styled-components";
import CheckboxDropdown, { type DefaultSelectOptions } from "../../shared/components/CheckboxDropdown";
import t from "../../shared/translations";
import type { IMessage } from "./query";

interface IProps {
    messages: IMessage[];
    selectedIds: Set<string>;
    onChangeSelection: (ids: string[]) => void;
}

type Opts = "read" | "unread";

const OPTIONS: { value: Opts; label: string }[] = [
    { value: "read", label: t("player.messages.select-dropdown-option-read") },
    { value: "unread", label: t("player.messages.select-dropdown-option-unread") },
];

const SelectMessagesContainer = styled.div`
    display: inline-block;
    margin-bottom: 0.5rem;
    margin-right: 1rem;
`;

function SelectMessages({ messages, selectedIds, onChangeSelection }: IProps): JSX.Element {
    const selected = useMemo<DefaultSelectOptions>(() => {
        if (messages.length === 0) {
            return "none";
        }
        let matchCount = 0;
        for (const message of messages) {
            if (selectedIds.has(message.id)) {
                matchCount++;
            }
        }
        if (matchCount === 0) {
            return "none";
        }
        if (matchCount === messages.length) {
            return "all";
        }
        return "some";
    }, [messages, selectedIds]);

    const handleSelect = useCallback(
        (value: Opts | DefaultSelectOptions) => {
            if (value === "none") {
                onChangeSelection([]);
            } else if (value === "all") {
                onChangeSelection(messages.map((msg) => msg.id));
            } else if (value === "read") {
                onChangeSelection(messages.filter((msg) => msg.read).map((msg) => msg.id));
            } else if (value === "unread") {
                onChangeSelection(messages.filter((msg) => !msg.read).map((msg) => msg.id));
            }
        },
        [messages, onChangeSelection],
    );

    if (messages.length === 0) {
        return null;
    }

    return (
        <SelectMessagesContainer>
            <CheckboxDropdown selected={selected} dropOptions={OPTIONS} onSelect={handleSelect} />
        </SelectMessagesContainer>
    );
}

export default SelectMessages;
